/** Container format detection from magic bytes (with file-extension fallback). */

export type FormatId =
  'png' | 'jpeg' | 'webp' | 'gif' | 'bmp' | 'avif' | 'tiff' | 'raw' | 'pfm' | 'pnm' | 'hdr' | 'exr' | 'fits' | 'unknown';

export const RAW_EXTENSIONS = [
  'dng',
  'cr2',
  'cr3',
  'crw',
  'nef',
  'nrw',
  'arw',
  'srf',
  'sr2',
  'orf',
  'rw2',
  'raf',
  'pef',
  'srw',
  '3fr',
  'fff',
  'iiq',
  'erf',
  'mef',
  'mos',
  'mrw',
  'x3f',
  'kdc',
  'dcr',
  'rwl',
  'raw',
  'dcs',
  'k25',
];

export const ACCEPT =
  'image/*,.png,.jpg,.jpeg,.webp,.gif,.bmp,.avif,.tif,.tiff,.pfm,.pgm,.ppm,.pnm,.pbm,.hdr,.pic,.rgbe,.exr,.fits,.fit,.fts,' +
  RAW_EXTENSIONS.map((e) => '.' + e).join(',');

function ascii(b: Uint8Array, o: number, n: number): string {
  let s = '';
  for (let i = 0; i < n && o + i < b.length; i++) s += String.fromCharCode(b[o + i]);
  return s;
}

/** Read TIFF IFD0 tag ids, to tell DNG / raw CFA files apart from ordinary TIFFs. */
export function tiffTagIds(b: Uint8Array): Map<number, number> {
  const tags = new Map<number, number>();
  if (b.length < 16) return tags;
  const le = b[0] === 0x49;
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const u16 = (o: number) => dv.getUint16(o, le);
  const u32 = (o: number) => dv.getUint32(o, le);
  const magic = u16(2);
  if (magic !== 42) return tags;
  const ifd = u32(4);
  if (ifd + 2 > b.length) return tags;
  const n = u16(ifd);
  for (let i = 0; i < n && ifd + 2 + i * 12 + 12 <= b.length; i++) {
    const e = ifd + 2 + i * 12;
    const tag = u16(e);
    const type = u16(e + 2);
    const val = type === 3 ? u16(e + 8) : u32(e + 8);
    tags.set(tag, val);
  }
  return tags;
}

export function detectFormat(b: Uint8Array, name = ''): FormatId {
  const ext = name.toLowerCase().split('.').pop() ?? '';
  if (b[0] === 0x89 && ascii(b, 1, 3) === 'PNG') return 'png';
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'jpeg';
  if (ascii(b, 0, 4) === 'RIFF' && ascii(b, 8, 4) === 'WEBP') return 'webp';
  if (ascii(b, 0, 4) === 'GIF8') return 'gif';
  if (ascii(b, 0, 2) === 'BM') return 'bmp';
  if (ascii(b, 4, 4) === 'ftyp') {
    const brand = ascii(b, 8, 4);
    if (brand === 'avif' || brand === 'avis') return 'avif';
    if (brand === 'crx ') return 'raw';
  }
  if (b[0] === 0x76 && b[1] === 0x2f && b[2] === 0x31 && b[3] === 0x01) return 'exr';
  if (ascii(b, 0, 10) === '#?RADIANCE' || ascii(b, 0, 6) === '#?RGBE') return 'hdr';
  if (ascii(b, 0, 9) === 'SIMPLE  =') return 'fits';
  if (ascii(b, 0, 15) === 'FUJIFILMCCD-RAW') return 'raw';
  if (b[0] === 0x50 && (b[1] === 0x46 || b[1] === 0x66) && (b[2] === 0x0a || b[2] === 0x0d || b[2] === 0x20)) return 'pfm';
  if (b[0] === 0x50 && b[1] >= 0x31 && b[1] <= 0x36 && /\s/.test(String.fromCharCode(b[2]))) return 'pnm';
  const tiffLE = b[0] === 0x49 && b[1] === 0x49;
  const tiffBE = b[0] === 0x4d && b[1] === 0x4d;
  if (tiffLE || tiffBE) {
    if (RAW_EXTENSIONS.includes(ext)) return 'raw';
    const magic = tiffLE ? b[2] | (b[3] << 8) : (b[2] << 8) | b[3];
    if (magic !== 42 && magic !== 43) return 'raw'; // ORF ('RO'/'RS'), RW2 (0x55) etc.
    const tags = tiffTagIds(b);
    // DNGVersion, or CFA / LinearRaw photometric interpretation.
    if (tags.has(50706) || tags.get(262) === 32803 || tags.get(262) === 34892) return 'raw';
    return 'tiff';
  }
  if (RAW_EXTENSIONS.includes(ext)) return 'raw';
  const byExt: Record<string, FormatId> = {
    png: 'png',
    jpg: 'jpeg',
    jpeg: 'jpeg',
    webp: 'webp',
    gif: 'gif',
    bmp: 'bmp',
    avif: 'avif',
    tif: 'tiff',
    tiff: 'tiff',
    pfm: 'pfm',
    pgm: 'pnm',
    ppm: 'pnm',
    pnm: 'pnm',
    hdr: 'hdr',
    exr: 'exr',
    fits: 'fits',
    fit: 'fits',
    fts: 'fits',
  };
  return byExt[ext] ?? 'unknown';
}
